import { uniq, uniqBy } from "lodash";
import { DurationUnit } from "~/enums/durationUnit";
import { BarLayoutOptions, VisualizationType } from "~/enums/visualizationType";
import Guid from "~/helpers/guid";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import Validatable from "~/mixins/validatable";
import { ComparisonType } from "~/models/conditionalRules/comparisonType";
import { ConditionalFieldDefinition } from "~/models/conditionalRules/conditionalFieldDefinition";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import ConditionalRuleSet from "~/models/conditionalRules/conditionalRuleSet";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import { TargetType } from "~/models/conditionalRules/targetType";
import { DatetimeMode } from "~/models/datetimeMode";
import VisualizationBuilderModel from "~/models/tracked/visualizationBuilderModel";
import VisualizationSourceDataPoint from "~/models/visualizations/visualizationSourceDataPoint";
import {
  DataPointStaticType,
  StaticDataPointLabels
} from "../../../formability/visualizationBuilder/enums/data-point-static-type";
import DateRangesUtils from "./dateRangesUtils";
import RangesUtils from "./rangesUtils";
import Visualization from "./visualization";
import VisualizationRange from "./visualizationRange";

interface ICustomizeDisplayOption {
  title: string;
  client_id: string;
}

export default abstract class VisualizationDataPoint extends Validatable(VisualizationBuilderModel) implements IConditionalRuleContainer, IConditionalRuleReferencedControl, ICustomizeDisplayOption {
  abstract readonly friendly_type = null;
  rootModel: Visualization;
  title: string;
  data_point_type;
  is_filtered: boolean;
  customize_display: boolean;
  enable_total: boolean;
  referenced_client_id: string;
  ranges: Hash<VisualizationRange> = {};
  referenced_col_client_id: string = "";
  referenced_row_client_id: string = "";
  selected_customize_display_options;
  units_of_time: DurationUnit = DurationUnit.minute;
  missing_data: boolean = false;
  tempHeaders: Array<Hash> = [];
  valueMap: Hash = {};
  rowTotals: Hash = {};
  colTotals: Hash = {};
  aggregation_type;

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
  }

  abstract addToRepo();
  abstract get source();
  abstract get sourceElement();
  abstract get visualizationSource();

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  // ******************** DISPLAY HELPERS

  abstract get isValid();

  abstract get sourcesContainsDataPoint();

  abstract get shouldShowFormat();

  abstract get shouldShowQueryFor();

  abstract get shouldShowCustomizeDisplay();

  abstract get shouldShowUnitsOfTime(): boolean;

  abstract get shouldShowSetRange(): boolean;

  abstract get shouldShowInclude(): boolean;

  abstract get customizeDisplayOptions(): ICustomizeDisplayOption[];

  abstract get customizeDisplayOptionsMap();

  abstract get elementType();

  abstract get decimalPlaces();

  abstract get allWorkflowSteps();

  abstract get workflowStepCompleters();

  abstract get assignedToCompleters();

  abstract get isPickList();

  abstract get isCurrency();

  abstract get allStatuses();

  get shouldShowAggregation(): boolean {
    return this.isValue;
  }

  get isValue(): boolean {
    const visualizationType = this.rootModel.visualization_type;
    if (visualizationType === VisualizationType.SummaryCard || visualizationType === VisualizationType.PivotTable) {
      return this.rootModel.valueDataPointsArray.some(pt => pt.referenced_client_id === this.referenced_client_id && pt.client_id === this.client_id);
    }

    if (this.rootModel.bar_layout === BarLayoutOptions.Vertical) {
      return this.isColumn;
    }

    return this.isRow;
  }

  get rangesArray(): VisualizationRange[] {
    return ObjectHelper.toArray(this.ranges);
  }

  get getMinRange() {
    return 0;
  }

  get getMaxRange() {
    return 50;
  }

  get getStepValue() {
    return 10;
  }

  get liveData() {
    return this.parent.liveData;
  }

  abstract get isMeasure(): boolean;

  abstract get isPublished(): boolean;

  abstract get isAccountRelationship(): boolean;

  abstract getDataHeaders(): string[];

  abstract get selectedDisplayOptions(): Hash[];

  get isRow(): boolean {
    return this.rootModel.rowDataPointsArray.some(pt => pt.referenced_client_id === this.referenced_client_id && pt.client_id === this.client_id);
  }

  get isColumn(): boolean {
    return this.rootModel.columnDataPointsArray.some(pt => pt.referenced_client_id === this.referenced_client_id && pt.client_id === this.client_id);
  }

  get liveRows() {
    const buckets = []
    const aggs = this.rootModel.liveData.filter(agg => agg["row_id"] === this.client_id);

    aggs.forEach(agg => {
      agg.row_agg_c.buckets.forEach(b => {
        buckets.push(b.key);
      })
    });

    return buckets;
  }

  get liveCols() {
    const buckets = [];
    const aggs = this.rootModel.liveData.filter(agg => agg["col_id"] === this.client_id);
          
    aggs.forEach(agg => {
      agg.row_agg_c.buckets.forEach(outerBucket => {
        outerBucket.col_agg_c.buckets.forEach(innerBucket => {
          buckets.push(innerBucket.key);
        });
      });
    });

    return buckets;
  }

  get accountRelationshipBuilderText() {
    return `Displaying all ${this.title.toLowerCase()}s`;
  }

  measureBuckets(min = this.getMinRange, max = this.getMaxRange, step = this.getStepValue) {
    let buckets = [];

    if (this.rangesArray.length >= 2 && this.isMeasure) {
      const rangesUtils = new RangesUtils(this.rangesArray);
      if (this.rootModel.hasData) {
        if (this.isRow) {
          return rangesUtils.maxRowBuckets(this.liveData);
        } else {
          return rangesUtils.maxColBuckets(this.liveData);
        }
      }
      for (let ind = 1; ind < rangesUtils.ranges.length; ind++) {
        const range = rangesUtils.ranges[ind];

        if (range.skip) {
          continue;
        }

        if (!range.repeat) {
          buckets.push(rangesUtils.rangeString(range));
        } else {
          const subBuckets = rangesUtils.rangeBuckets(range).map(r => r.range);
          
          buckets = buckets.concat(subBuckets);
        }
      }

      return uniq(buckets);
    } else if (this.rangesArray.length >= 2 && !this.isMeasure) {
      const rangesUtils = new DateRangesUtils(this.rangesArray);
      if (this.rootModel.hasData) {
        if (this.isRow) {
          return rangesUtils.liveRowBuckets(this.liveData);
        } else {
          return rangesUtils.liveColBuckets(this.liveData);
        }
      }
      for (let ind = 1; ind < rangesUtils.ranges.length; ind++) {
        const range = rangesUtils.ranges[ind];

        if (range.skip) {
          continue;
        }

        if (!range.repeat) {
          buckets.push(rangesUtils.rangeString(range));
        } else {
          const subBuckets = rangesUtils.rangeBuckets(range).map(r => r.range);
          buckets = buckets.concat(subBuckets);
        }
      }

      return buckets;
    }

    if (this.rootModel.visualization_type === VisualizationType.PivotTable) {
      if (this.rootModel.hasData) {
        if (this.isRow) {
          buckets = this.liveRows;
        } else if (this.isColumn) {
          buckets = this.liveCols;
        }

        return uniq(buckets.sort((a, b) => parseFloat(a) - parseFloat(b)));
      }
    }

    // Set default buckets if no ranges found
    for (let i = min; i < max; i += step) {
      buckets.push(`${i} - ${i + step - 1}`);
    }

    return buckets;
  }

  addToTempHeaders(header: string) {
    this.tempHeaders.push({
      title: header,
      client_id: this.client_id
    });

    this.tempHeaders = uniqBy(this.tempHeaders, h => h.title);
  }

  get configuredTempHeaders() {
    let pointHeaders = this.tempHeaders.slice();

    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allWorkflowSteps]) {
      const unusedWorkflowSteps = this.allWorkflowSteps.filter(step => !this.tempHeaders.some(h => h.title === step.titleOrDefault));
      unusedWorkflowSteps.forEach(step => pointHeaders.push({ title: step.titleOrDefault, client_id: this.client_id }));
    }

    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allStatuses]) {
      const unusedStatuses = this.allStatuses.filter(status => !this.tempHeaders.some(h => h.title === status.name));
      unusedStatuses.forEach(status => pointHeaders.push({ title: status.name, client_id: this.client_id }));
    }

    if (this.shouldSortHeaders) {
      pointHeaders = pointHeaders.sort((a,b) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0))
    }

    if (this.isRow) {
      pointHeaders.unshift({title: this.title, client_id: this.client_id, isHeader: true});
    }

    if (this.enable_total) {
      pointHeaders.push({title: "Total", client_id: this.client_id});
    }

    return pointHeaders.filter(p => {
      let alternateId = "";
      
      if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allWorkflowSteps]) {
        const step = this.allWorkflowSteps.find(ws => ws.title === p.title);
        alternateId = step ? step.client_id : "";
      }

      if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.workflowStepCompleter] ||
            this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.assignedTo] ||
            this.isPickList) {
        alternateId = p.title;
      }

      if (this.customize_display && this.isAccountRelationship && !p.isHeader) {
        return p.title && this.selected_customize_display_options.some(o => o === p.title || o === alternateId);
      }

      return p.title && !this.selected_customize_display_options.some(o => o === p.client_id || o === alternateId);
    });
  }

  get shouldSortHeaders() {
    return this.referenced_client_id !== StaticDataPointLabels[DataPointStaticType.allWorkflowSteps]
      && this.referenced_client_id !== StaticDataPointLabels[DataPointStaticType.allStatuses]
      && this.rangesArray.length === 0;
  }

  addToValueMap(value: string | number, rowKey, colKey) {
    this.valueMap[rowKey + "-" + colKey] = value;

    if (!this.rowTotals[rowKey]) {
      this.rowTotals[rowKey] = 0;
    }

    this.rowTotals[rowKey] += value;

    if (!this.colTotals[colKey]) {
      this.colTotals[colKey] = 0;
    }

    this.colTotals[colKey] += value;
  }

  getValue(rowKey, colKey) {
    return this.valueMap[rowKey + "-" + colKey] || '--';
  }

  abstract isFusedWith(dataPoint: VisualizationSourceDataPoint): boolean;

  staticLabels(keys) {
    return keys.map(k => StaticDataPointLabels[k]);
  }

  abstract displayConditionType(targetType: TargetType): ComparisonType;

  abstract getChoiceComparisonOptions(): any[];

  removeOption(option) {
    if (this.isAccountRelationship) {
      this.selected_customize_display_options = this.selected_customize_display_options.filter(o => o !== option);
    } else {
      this.selected_customize_display_options.push(option);
      this.selected_customize_display_options = this.selected_customize_display_options.slice();
    }
  }

  getConditionDateTimeMode(): DatetimeMode {
    return DatetimeMode.Datetime;
  }

  getConditionMinDateTime(): string {
    return '';
  }

  getConditionMaxDateTime(): string {
    return '';
  }

  targetTypes(): TargetType[] {
    return [];
  }

  isValidForConditionalLogic: boolean;
  referenceNameOrDefault: string;

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ConditionalRuleSet {
    return undefined;
  }

  getConditionalRuleSets(fieldName: string): Hash<ConditionalRuleSet> {
    return undefined;
  }

  getReferencableControls(): IConditionalRuleReferencedControl[] {
    return [];
  }

  getReferencedControl(id: string): IConditionalRuleReferencedControl {
    return undefined;
  }

  getSortedConditionalRuleSets(fieldName: string): ConditionalRuleSet[] {
    return [];
  }

  // ******************** TRACK CHANGES END

}
